import { storage } from 'utils';
import * as api from 'utils/api';

interface GetMegaMenuLink {
  text: string;
  link: string;
  subItems: Array<GetMegaMenuLink & { subItems?: GetMegaMenuLink[] }>;
}

export interface GetMegaMenuItem {
  value: string;
  text: string;
  links: GetMegaMenuLink[];
}

interface IGetMegaMenuResponse {
  items: Record<
    string,
    {
      value: string;
      text: string;
      links: GetMegaMenuLink[];
    }
  >;
  itemOrder: string[];
}

export interface MegaMenuItem {
  id: string;
  href: string;
  text: string;
  subItems?: MegaMenuItem[];
}

export type MenuStorageItem = {
  fetchTs: number;
  menu: MegaMenuItem[];
};

const MenuStorage = storage(localStorage);

export default class WebcoreService {
  static MENU_STORAGE_KEY = 'sv.webcore.menu';

  /**
   * api request adapter object
   */
  private adapter: api.Adapter;

  constructor() {
    this.adapter = new api.Adapter({
      baseURL: this.getUrlForEnvironment('prod'),
      responseType: 'json',
      headers: {
        timeout: 60 * 10 * 1000,
        'content-type': 'application/json',
        accept: 'application/json'
      }
    });
  }

  private getUrlForEnvironment(env: string) {
    const isProd = env.indexOf('prod') !== -1;
    const isStage = env.indexOf('stage') !== -1;
    const subDomain = isProd || isStage ? 'transaction-services' : 'transaction-services-nonprod';
    const api = isProd ? 'api' : `api-${env}`;
    return `https://${api}.webcore.${subDomain}.service.vic.gov.au`;
  }

  private getMenuFromCache() {
    try {
      const content = MenuStorage.getItem(WebcoreService.MENU_STORAGE_KEY) as MenuStorageItem;
      const fifteenMinutes = 15 * 60 * 1000;

      if (
        'fetchTs' in content &&
        'menu' in content &&
        typeof content.fetchTs === 'number' &&
        content.fetchTs + fifteenMinutes > Date.now()
      ) {
        // verify that the shape/datatype is correct, and that the fetch isn't stale

        return content.menu;
      }
    } catch (e) {
      // no-op, let it fall through
    }

    return null;
  }

  private cacheMenu(menu: MegaMenuItem[]) {
    MenuStorage.setItem(WebcoreService.MENU_STORAGE_KEY, { fetchTs: Date.now(), menu });
  }

  private mapResponseToMegamenu(response: IGetMegaMenuResponse): MegaMenuItem[] {
    const items = Object.values(response.items).reduce((result, currentValue) => {
      result[currentValue.text] = currentValue;
      return result;
    }, {} as Record<string, typeof response.items[0]>);

    const convertLinksToSubItems = (links: GetMegaMenuLink[]) => {
      return links.map(link => ({
        id: link.text,
        text: link.text,
        href: link.link,
        subItems: link.subItems ? convertLinksToSubItems(link.subItems) : undefined
      }));
    };

    return response.itemOrder.map(text => {
      const menuItem = items[text];

      return {
        id: menuItem.value,
        text: menuItem.text,
        href: '',
        subItems: convertLinksToSubItems(menuItem.links)
      };
    });
  }

  public setEnvironment(env: string) {
    this.adapter.updateConfig({
      baseURL: this.getUrlForEnvironment(env)
    });
  }

  public async getMegaMenuItems(useCache = true): Promise<MegaMenuItem[]> {
    if (useCache) {
      const cachedMenu = this.getMenuFromCache();

      if (cachedMenu !== null) {
        return cachedMenu;
      }
    }
    const { data } = await this.adapter.getValue<IGetMegaMenuResponse>('/v1/megamenu');

    const menu = this.mapResponseToMegamenu(data);

    if (useCache) {
      this.cacheMenu(menu);
    }

    return menu;
  }
}
